import React, { useState, useEffect } from 'react';
import { Calendar, Clock, FileText, User, Eye } from 'lucide-react';
import './style.css';

const MyLeavesCard = ({ 
  leaves = [], 
  onCancel,  
  itemsPerPage,
  cancellingIds = new Set() // Accept from parent
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil(leaves.length / itemsPerPage);
  const displayedLeaves = leaves.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  // 🔁 Reset to first page when leaves data changes
  useEffect(() => {
    setCurrentPage(0);
  }, [leaves]);

  const leaveTypeLabels = {
    SICK_LEAVE: 'Sick Leave',
    ANNUAL_LEAVE: 'Annual Leave',
    UNPAID_LEAVE: 'Unpaid Leave',
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'leave-status-approved';
      case 'rejected':
        return 'leave-status-rejected';
      case 'pending':
        return 'leave-status-pending';
      case 'cancelled':
        return 'leave-status-cancelled';
      default:
        return 'leave-status-default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return '✓';
      case 'rejected':
        return '✗';
      case 'pending':
        return '⏳';
      case 'cancelled':
        return '⚪';
      default:
        return '❓';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // const calculateDuration = (startDate, endDate) => {
  //   if (!startDate || !endDate) return 'N/A';
  //   const start = new Date(startDate);
  //   const end = new Date(endDate);
  //   const diffTime = Math.abs(end - start);
  //   const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  //   return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  // };

  if (!leaves || leaves.length === 0) {
    return (
      <div className="leaves-card-empty">
        <div className="leaves-card-header">
          <div className="leaves-card-title-section">
            <Calendar color="#059669" size={24} />
            <h2 className="leaves-card-title">My Leaves</h2>
          </div>
        </div>
        <div className="leaves-card-empty-content">
          <Calendar color="#d1d5db" size={48} className="leaves-card-empty-icon" />
          <p className="leaves-card-empty-title">No leave requests found</p>
          <p className="leaves-card-empty-subtitle">Your leave requests will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="leaves-card">
      {/* Header */}
      <div className="leaves-card-header leaves-card-header-gradient">
        <div className="leaves-card-header-content">
          <div className="leaves-card-title-section">
            <Calendar color="#059669" size={24} />
            <h2 className="leaves-card-title">My Leaves</h2>
          </div>
          <div className="leaves-card-count">
            {leaves.length} total request{leaves.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Leave Items */}
      <div>
        {displayedLeaves.map((leave, index) => {
          // ✅ Compute isDisabled INSIDE the map where `leave` exists
          const isDisabled = 
            leave.status?.toLowerCase() === 'approved' ||
            leave.status?.toLowerCase() === 'rejected' ||
            leave.status?.toLowerCase() === 'cancelled' ||
            cancellingIds.has(leave.requestId);

          return (
            <div key={leave.requestId || index} className="leave-item">
              {/* Status and Type Row */}
              <div className="leave-item-header">
                <div className="leave-item-badges">
                  <span className={`leave-status-badge ${getStatusClass(leave.status)}`}>
                    {getStatusIcon(leave.status)} {leave.status?.toUpperCase() || 'UNKNOWN'}
                  </span>
                  <span className="leave-type-badge">
                    {leaveTypeLabels[leave.type] || leave.type || 'N/A'}
                  </span>
                </div>
                <div className="leave-created-date">
                  Created: {formatDate(leave.createdAt)}
                </div>
              </div>

              {/* Dates and Duration */}
              <div className="leave-dates-grid">
                <div className="leave-date-item">
                  <Calendar color="#9ca3af" size={16} style={{ marginBottom: '25px' }} />
                  <div>
                    <p className="leave-date-label">Start Date</p>
                    <p className="leave-date-value">{formatDate(leave.startDate)}</p>
                  </div>
                </div>
                <div className="leave-date-item">
                  <Calendar color="#9ca3af" size={16} style={{ marginBottom: '25px' }} />
                  <div>
                    <p className="leave-date-label">End Date</p>
                    <p className="leave-date-value">{formatDate(leave.endDate)}</p>
                  </div>
                </div>
                <div className="leave-date-item">
                  <Clock color="#9ca3af" size={16} style={{ marginBottom: '25px' }} />
                  <div>
                    <p className="leave-date-label">Duration</p>
                    {/* <p className="leave-date-value">{calculateDuration(leave.startDate, leave.endDate)}</p> */}
                    <p className="leave-date-value">
                      {leave.days} {  leave.days === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Justification */}
              {leave.justification && (
                <div className="leave-section">
                  <div className="leave-section-header">
                    <User color="#9ca3af" size={16} />
                    <p className="leave-section-title">Justification</p>
                  </div>
                  <p className="leave-section-content leave-justification-content" style={{ width: '100%' }}>
                    {leave.justification}
                  </p>
                </div>
              )}

              {/* Content */}
              {/* {leave.content && (
                <div className="leave-section">
                  <div className="leave-section-header">
                    <User color="#9ca3af" size={16} />
                    <p className="leave-section-title">Documents</p>
                  </div>
                  <p className="leave-section-content leave-notes-content">
                    {leave.content}
                  </p>
                </div>
              )} */}
              {leave.content && (
                <div className="leave-section">
                  <div className="leave-section-header">
                    <FileText color="#9ca3af" size={16} />
                    <p className="leave-section-title">Documents</p>
                  </div>
                  <div className="leave-section-content leave-notes-content">
                  {/* Assume leave.content is a valid URL to a document */}
                    <a
                      href={leave.content}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="document-link"
                      onClick={(e) => {
                      // Optional: Add logging, token refresh, or redirect through your API
                      // e.g., e.preventDefault(); openSecureDocument(leave.content);
                     }}
                    >
                      View Attached Document
                    </a>
                  </div>
               </div>
              )}

              {/* Cancel Button (conditionally disabled) */}
              <div className="leave-cancel-button-wrapper">
                <button
                  onClick={() => onCancel(leave.requestId)}
                  disabled={isDisabled}
                  className={`leave-cancel-button ${isDisabled ? 'leave-cancel-button-disabled' : ''}`}
                >
                  {cancellingIds.has(leave.requestId) ? 'Cancelling...' : 'Cancel'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {leaves.length > itemsPerPage && (
        <div className="carousel-nav">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
            disabled={currentPage === 0}
            className="carousel-button"
          >
            ◀ Previous
          </button>
          <span className="carousel-page-indicator">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1))}
            disabled={currentPage === totalPages - 1}
            className="carousel-button"
          >
            Next ▶
          </button>
        </div>
      )}
    </div>
  );
};

export default MyLeavesCard;